import { Dispatch } from "react";
import { useTranslation } from "react-i18next";
import {
  JsonSchemaProperty,
  JsonSchemaPropertyMap,
  RowDataObject,
  RowDataReducerAction,
  RowDataReducerState,
  SetupBaseRequestInfo,
  RenderInputTypeParams,
  ToastInfo,
  DependencyRules,
  CoRelationDependencyRules,
} from "../../../../types";
import { calculateTotal, getTitleFromKey } from "../../helpers";
import { ROW_DATA_KEYS } from "../../constants";
import ShippingInfoRows from "../custom-renderers/po-groups/ShippingInfoRows";
import RenderCorrelationTableRows from "./correlation-grid/RenderCorrelationTableRows";
import TotalFooter from "../custom-renderers/footer/TotalFooter";
import i18n from "../../../../translate/i18next";
import RenderCoRelationRows from "../renderers/RenderCoRelationRows";

export type ArraySectionRendererProps = {
  propertyKey: string;
  property: JsonSchemaProperty;
  properties: JsonSchemaPropertyMap;
  inputValueObject: RowDataObject[];
  rows: number[];
  isAddButtonDisabled: boolean;
  isDeleteButtonDisabled: boolean;
  isReadOnly: boolean;
  rowDataReducerState: RowDataReducerState;
  setupBaseRequestInfo: SetupBaseRequestInfo;
  selectedTabSectionKey: string | undefined;
  dependencyRules: DependencyRules | CoRelationDependencyRules | undefined;
  addRow: () => void;
  deleteRow: (rowIndex: number) => void;
  updateRow: (rowIndex: number, newFieldValue: RowDataObject) => void;
  renderInputType: (params: RenderInputTypeParams) => JSX.Element;
  canAddSingleRow: (rowIndex: number) => boolean;
  setRecordPageToast: (toast: ToastInfo) => void;
  dispatch: Dispatch<RowDataReducerAction>;
};

type ArraySectionRenderer = (props: ArraySectionRendererProps) => JSX.Element;

const GroupAddressesSection: ArraySectionRenderer = ({
  propertyKey,
  properties,
  inputValueObject,
  isAddButtonDisabled,
  isReadOnly,
  rowDataReducerState,
  setupBaseRequestInfo,
  selectedTabSectionKey,
  addRow,
  deleteRow,
  updateRow,
  renderInputType,
  canAddSingleRow,
  setRecordPageToast,
  dispatch,
}) => (
  <ShippingInfoRows
    inputValueObject={inputValueObject}
    // some reason the updated rows from [rows, setRows] useState is not getting updated
    rows={inputValueObject ? Array.from(inputValueObject.keys()) : []}
    propertyKey={propertyKey}
    properties={properties}
    setupBaseRequestInfo={setupBaseRequestInfo}
    isAddButtonDisabled={isAddButtonDisabled}
    isReadOnly={isReadOnly}
    selectedTabSectionKey={selectedTabSectionKey}
    rowDataReducerState={rowDataReducerState}
    addRow={addRow}
    deleteRow={deleteRow}
    updateRow={updateRow}
    renderInputType={renderInputType}
    canAddSingleRow={canAddSingleRow}
    setRecordPageToast={setRecordPageToast}
    dispatch={dispatch}
  />
);

const DistributionSection: ArraySectionRenderer = ({
  propertyKey,
  property,
  inputValueObject,
  rows,
  isAddButtonDisabled,
  isDeleteButtonDisabled,
  isReadOnly,
  addRow,
  deleteRow,
  renderInputType,
  dispatch,
}) => {
  const { t } = useTranslation();
  const percentageTotal = calculateTotal({
    inputValues: inputValueObject,
    fieldKey: ROW_DATA_KEYS.DISTRIBUTION_PERCENTAGE,
  });

  return (
    <>
      <RenderCoRelationRows
        inputValueObject={inputValueObject}
        rows={rows}
        propertyKey={propertyKey}
        property={property}
        isAddButtonDisabled={isAddButtonDisabled}
        isDeleteButtonDisabled={isDeleteButtonDisabled}
        isReadOnly={isReadOnly}
        addRow={addRow}
        deleteRow={deleteRow}
        renderInputType={renderInputType}
        dispatch={dispatch}
      />
      <TotalFooter
        total={percentageTotal}
        title={getTitleFromKey(
          ROW_DATA_KEYS.DISTRIBUTION_PERCENTAGE,
          t,
          i18n.language,
        )}
        isInvalid={percentageTotal !== 100}
        suffix="%"
      />
    </>
  );
};

const RecurringEntryReferenceSection: ArraySectionRenderer = ({
  propertyKey,
  property,
  inputValueObject,
  rows,
  isAddButtonDisabled,
  isDeleteButtonDisabled,
  isReadOnly,
  rowDataReducerState,
  dependencyRules,
  addRow,
  deleteRow,
  renderInputType,
  dispatch,
}) => {
  const { t } = useTranslation();
  const total = calculateTotal({
    inputValues: inputValueObject,
    fieldKey: ROW_DATA_KEYS.POSTING_AMOUNT,
  });

  return (
    <>
      <RenderCorrelationTableRows
        inputValueObject={inputValueObject}
        rows={rows}
        propertyKey={propertyKey}
        property={property}
        isAddButtonDisabled={isAddButtonDisabled}
        isDeleteButtonDisabled={isDeleteButtonDisabled}
        isReadOnly={isReadOnly}
        rowDataReducerState={rowDataReducerState}
        dependencyRules={dependencyRules}
        addRow={addRow}
        deleteRow={deleteRow}
        renderInputType={renderInputType}
        dispatch={dispatch}
        title={getTitleFromKey(propertyKey, t, i18n.language)}
        tableHeight={"22rem"}
      />
      <TotalFooter
        total={total}
        title={getTitleFromKey(ROW_DATA_KEYS.POSTING_AMOUNT, t, i18n.language)}
        prefix="$"
      />
    </>
  );
};

const JournalTemplateEntryReferenceSection: ArraySectionRenderer = ({
  propertyKey,
  property,
  inputValueObject,
  rows,
  isAddButtonDisabled,
  isDeleteButtonDisabled,
  isReadOnly,
  rowDataReducerState,
  dependencyRules,
  addRow,
  deleteRow,
  renderInputType,
  dispatch,
}) => {
  const { t } = useTranslation();

  const total = calculateTotal({
    inputValues: inputValueObject,
    fieldKey: ROW_DATA_KEYS.DEFAULT_AMOUNT,
  });

  return (
    <>
      <RenderCorrelationTableRows
        inputValueObject={inputValueObject}
        rows={rows}
        propertyKey={propertyKey}
        property={property}
        isAddButtonDisabled={isAddButtonDisabled}
        isDeleteButtonDisabled={isDeleteButtonDisabled}
        isReadOnly={isReadOnly}
        rowDataReducerState={rowDataReducerState}
        dependencyRules={dependencyRules}
        addRow={addRow}
        deleteRow={deleteRow}
        renderInputType={renderInputType}
        dispatch={dispatch}
        title={getTitleFromKey(propertyKey, t, i18n.language)}
        tableHeight={"25rem"}
      />
      <TotalFooter
        total={total}
        title={getTitleFromKey(ROW_DATA_KEYS.DEFAULT_AMOUNT, t, i18n.language)}
        prefix="$"
      />
    </>
  );
};

// To add a new special-cased array section: add one entry here.
// No changes needed in RenderCoRelation itself.
export const ARRAY_SECTION_RENDERERS: Record<string, ArraySectionRenderer> = {
  [ROW_DATA_KEYS.GROUP_ADDRESSES]: GroupAddressesSection,
  [ROW_DATA_KEYS.DISTRIBUTION]: DistributionSection,
  [ROW_DATA_KEYS.RECURRING_ENTRY_REFERENCE]: RecurringEntryReferenceSection,
  [ROW_DATA_KEYS.JOURNAL_TEMPLATE_ENTRY_REFERENCE]:
    JournalTemplateEntryReferenceSection,
};
